import React, { FC, HTMLProps, useEffect, useMemo, useState } from "react";
import MultipleInlineCalendar, {
  MultipleInlineCalendarProps,
} from "./MultipleInlineCalendar";
import stl from "./MultipleDropdownCalendar.module.scss";
import Dropdown from "../Dropdown";
import { useStore } from "effector-react";
import { $currentLocale } from "../../store/shared/locales";
import format from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import cloneDeep from "lodash/cloneDeep";
import cn from "classnames";
import { AppConfig } from "../../config";
import { useTranslation } from "react-i18next";

interface Props extends HTMLProps<HTMLDivElement> {
  calendarProps: MultipleInlineCalendarProps;
}

const MultipleDropdownCalendar: FC<Props> = ({ calendarProps, ...props }) => {
  const { t } = useTranslation(AppConfig.translationFileNames.base);

  const { dateFnsLocale: locale } = useStore($currentLocale);

  // its a bug solution.
  // somewhy when dropdown opens for first time - something sets
  // "display: none !important" to lots of elements for .5s, then returns all back.
  // hope you will find the reason of such behavior :D
  // or keep this peace of hack :D
  // (may be related to lazy import, "debugger" revealed that Progress from Suspense is rendered at that bug time)
  const [isDatesDropdownOpen, setIsDatesDropdownOpen] = useState<boolean>(true);
  useEffect(() => {
    setTimeout(() => {
      setIsDatesDropdownOpen(false);
    }, 100);
  }, []);

  const { value, onChange } = calendarProps;

  const formattedDates = useMemo<{ date: Date; formatted: string }[]>(() => {
    if (!value) return [];
    return value.map((date) => {
      return { date: date, formatted: format(date, "dd-MM", { locale }) };
    });
  }, [value, locale]);

  const isDatesDeletable = useMemo<boolean>(() => {
    return (value && value.length > 1) ?? false;
  }, [value]);

  const isCalendarDisabled = useMemo<boolean>(() => {
    if (AppConfig.maxDateRange === 1) return false;
    return (value && value.length >= AppConfig.maxDateRange) ?? false;
  }, [value]);

  const deleteDate = (date: Date) => {
    if (!isDatesDeletable) return;

    const clonedValue = cloneDeep(value);
    if (clonedValue) {
      const idx = clonedValue.findIndex((valueDate) => {
        return isSameDay(date, valueDate);
      });
      if (~idx) {
        clonedValue.splice(idx, 1);
      }
    }
    onChange?.(clonedValue ?? null);
  };

  return (
    <Dropdown
      overlayClassName={stl.dropdown}
      trigger={"click"}
      visible={isDatesDropdownOpen}
      onVisibleChange={setIsDatesDropdownOpen}
      overlay={
        <>
          <MultipleInlineCalendar
            {...calendarProps}
            disabledDays={
              isCalendarDisabled
                ? () => isCalendarDisabled
                : calendarProps.disabledDays
            }
            maxValueLength={AppConfig.maxDateRange}
          />
          {AppConfig.maxDateRange > 1 && (
            <span className={stl.tip}>
              * {t("dateSelectRange", { v: AppConfig.maxDateRange })}
            </span>
          )}
        </>
      }
    >
      <div {...props} className={cn(stl.tags, props.className)} tabIndex={0}>
        {formattedDates.map(({ date, formatted }) => (
          <div
            tabIndex={0}
            key={formatted}
            className={cn(stl.tag, {
              [stl.tagDeletable]: isDatesDeletable,
            })}
            onClick={(event) => {
              event.stopPropagation();
              deleteDate(date);
            }}
          >
            {formatted}
          </div>
        ))}
        {formattedDates.length === 0 && (
          <span className={stl.placeholder}>{t("dateSelectQuestion")}</span>
        )}
      </div>
    </Dropdown>
  );
};

export default MultipleDropdownCalendar;
